import type { AgencyGateway } from '../application/agencyGateway';
import type { AccessResult } from '../domain/access';
import type { AgencyBlogPost, AgencyBlogResult, AgencyBlogSaveResult, AgencyContext, AgencyContextResult, AgencyInventoryResult, AgencyListing, AgencyOpenDmResult, AgencyRelationship, AgencyRelationshipsResult, AgencyTeamMember, AgencyTeamResult } from '../domain/agency';
import type { ConversationMessage, ConversationMessagesResult, SendMessageResult } from '../domain/workspaces';
import type { SupabaseAccessGateway } from './supabaseAccessGateway';

const sessionKey = 'alpha-mls:supabase-session:v1';
const env = import.meta.env as Record<string, string | undefined>;

interface StoredSession { readonly accessToken: string; readonly expiresAt: number; }
interface ContextRow {
  readonly executor_display_name: string; readonly represented_agent_id: string; readonly represented_public_name: string;
  readonly license_number: string | null; readonly agent_phone: string | null; readonly agent_email: string | null;
  readonly agent_city: string; readonly agent_state: string; readonly specialties: string[]; readonly headline: string | null;
  readonly bio: string | null; readonly service_areas: string[]; readonly agency_id: string; readonly agency_name: string;
  readonly agency_phone: string | null; readonly agency_website: string | null; readonly agency_city: string;
  readonly agency_state: string; readonly professional_role: string | null; readonly membership_role: string | null;
  readonly can_manage: boolean; readonly team_count: number | string; readonly active_inventory: number | string;
  readonly customers: number | string; readonly owners: number | string; readonly visible_conversations: number | string;
}
interface TeamRow {
  readonly agent_id: string; readonly public_name: string; readonly license_number: string | null; readonly phone: string | null;
  readonly email: string | null; readonly city: string; readonly state: string; readonly specialties: string[];
  readonly headline: string | null; readonly bio: string | null; readonly service_areas: string[];
  readonly professional_role: string | null; readonly is_self: boolean; readonly direct_conversation_id: string | null;
}
interface ListingRow {
  readonly listing_id: string; readonly title: string; readonly transaction_type: 'sale' | 'rent';
  readonly price: number | string; readonly currency: string; readonly property_type: string; readonly city: string;
  readonly state: string; readonly listing_role: string;
}
interface RelationshipRow {
  readonly client_id: string; readonly display_name: string; readonly relationship_type: string;
  readonly party_role: string; readonly status: string;
}
interface BlogRow {
  readonly post_id: string;
  readonly title: string;
  readonly subtitle: string | null;
  readonly body: string;
  readonly status: 'draft' | 'published' | 'archived';
  readonly published_at: string | null;
  readonly updated_at: string;
  readonly can_manage: boolean;
}

interface MessageRow {
  readonly message_id: string; readonly body: string; readonly message_type: string; readonly created_at: string;
  readonly sender_agent_id: string; readonly sender_name: string; readonly is_mine: boolean;
}

function readAccessToken(): string | null {
  try {
    const raw = window.localStorage.getItem(sessionKey);
    if (raw === null) return null;
    const parsed = JSON.parse(raw) as Partial<StoredSession>;
    if (typeof parsed.accessToken !== 'string' || typeof parsed.expiresAt !== 'number' || parsed.expiresAt <= Math.floor(Date.now() / 1000)) return null;
    return parsed.accessToken;
  } catch {
    return null;
  }
}

export class SupabaseAgencyGateway implements AgencyGateway {
  constructor(private readonly accessGateway: SupabaseAccessGateway) {}

  resolveCurrentIdentity(signal?: AbortSignal): Promise<AccessResult> { return this.accessGateway.resolveCurrentIdentity(signal); }
  signOut(signal?: AbortSignal): Promise<void> { return this.accessGateway.signOut(signal); }

  private async rpc<T>(name: string, body: Record<string, unknown>, signal?: AbortSignal): Promise<T | null> {
    const url = env.VITE_SUPABASE_URL?.replace(/\/$/u, '');
    const key = env.VITE_SUPABASE_PUBLISHABLE_KEY;
    const token = readAccessToken();
    if (url === undefined || key === undefined || token === null) return null;
    const response = await fetch(url + '/rest/v1/rpc/' + name, {
      method: 'POST',
      headers: { apikey: key, Authorization: 'Bearer ' + token, 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
      ...(signal === undefined ? {} : { signal }),
    });
    if (!response.ok) return null;
    return (await response.json()) as T;
  }

  async loadContext(signal?: AbortSignal): Promise<AgencyContextResult> {
    try {
      const rows = await this.rpc<ContextRow[]>('agency_context_v1', {}, signal);
      const row = rows?.[0];
      if (row === undefined) return { ok: false, message: 'No fue posible resolver la identidad de Agency.' };
      const context: AgencyContext = {
        executorDisplayName: row.executor_display_name, representedAgentId: row.represented_agent_id,
        representedPublicName: row.represented_public_name, licenseNumber: row.license_number, agentPhone: row.agent_phone,
        agentEmail: row.agent_email, agentCity: row.agent_city, agentState: row.agent_state, specialties: row.specialties,
        headline: row.headline, bio: row.bio, serviceAreas: row.service_areas, agencyId: row.agency_id, agencyName: row.agency_name,
        agencyPhone: row.agency_phone, agencyWebsite: row.agency_website, agencyCity: row.agency_city, agencyState: row.agency_state,
        professionalRole: row.professional_role, membershipRole: row.membership_role, canManage: row.can_manage,
        teamCount: Number(row.team_count), activeInventory: Number(row.active_inventory), customers: Number(row.customers),
        owners: Number(row.owners), visibleConversations: Number(row.visible_conversations),
      };
      return { ok: true, context };
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') throw error;
      return { ok: false, message: 'No fue posible conectar con Agency.' };
    }
  }

  async loadTeam(signal?: AbortSignal): Promise<AgencyTeamResult> {
    try {
      const rows = await this.rpc<TeamRow[]>('agency_team_v1', {}, signal);
      if (rows === null) return { ok: false, message: 'No fue posible leer el equipo.' };
      const team: AgencyTeamMember[] = rows.map((row) => ({
        agentId: row.agent_id, publicName: row.public_name, licenseNumber: row.license_number, phone: row.phone, email: row.email,
        city: row.city, state: row.state, specialties: row.specialties, headline: row.headline, bio: row.bio,
        serviceAreas: row.service_areas, professionalRole: row.professional_role, isSelf: row.is_self,
        directConversationId: row.direct_conversation_id,
      }));
      return { ok: true, team };
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') throw error;
      return { ok: false, message: 'No fue posible conectar con el equipo.' };
    }
  }

  async loadInventory(agentId: string, signal?: AbortSignal): Promise<AgencyInventoryResult> {
    try {
      const rows = await this.rpc<ListingRow[]>('agency_inventory_v1', { p_agent_id: agentId, p_limit: 12 }, signal);
      if (rows === null) return { ok: false, message: 'No fue posible leer el inventario permitido.' };
      const listings: AgencyListing[] = rows.map((row) => ({
        listingId: row.listing_id, title: row.title, transactionType: row.transaction_type, price: Number(row.price),
        currency: row.currency.trim(), propertyType: row.property_type, city: row.city, state: row.state, listingRole: row.listing_role,
      }));
      return { ok: true, listings };
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') throw error;
      return { ok: false, message: 'No fue posible conectar con el inventario.' };
    }
  }

  async loadRelationships(agentId: string, signal?: AbortSignal): Promise<AgencyRelationshipsResult> {
    try {
      const rows = await this.rpc<RelationshipRow[]>('agency_relationships_v1', { p_agent_id: agentId, p_limit: 24 }, signal);
      if (rows === null) return { ok: false, message: 'No fue posible leer Customers y Owners.' };
      const relationships: AgencyRelationship[] = rows.map((row) => ({
        clientId: row.client_id, displayName: row.display_name, relationshipType: row.relationship_type,
        partyRole: row.party_role, status: row.status,
      }));
      return { ok: true, relationships };
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') throw error;
      return { ok: false, message: 'No fue posible conectar con las relaciones.' };
    }
  }

  async loadBlog(agentId: string, signal?: AbortSignal): Promise<AgencyBlogResult> {
    try {
      const rows = await this.rpc<BlogRow[]>('agency_blog_posts_v1', { p_agent_id: agentId, p_limit: 20 }, signal);
      if (rows === null) return { ok: false, message: 'No fue posible leer el Blog.' };
      const posts: AgencyBlogPost[] = rows.map((row) => ({
        postId: row.post_id,
        title: row.title,
        subtitle: row.subtitle,
        body: row.body,
        status: row.status,
        publishedAt: row.published_at,
        updatedAt: row.updated_at,
        canManage: row.can_manage,
      }));
      return { ok: true, posts };
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') throw error;
      return { ok: false, message: 'No fue posible conectar con el Blog.' };
    }
  }

  async saveBlog(
    agentId: string,
    postId: string | null,
    title: string,
    subtitle: string,
    body: string,
    publish: boolean,
    signal?: AbortSignal,
  ): Promise<AgencyBlogSaveResult> {
    try {
      const savedId = await this.rpc<string>('agency_blog_save_v1', {
        p_agent_id: agentId,
        p_post_id: postId,
        p_title: title,
        p_subtitle: subtitle,
        p_body: body,
        p_publish: publish,
      }, signal);
      return savedId === null
        ? { ok: false, message: 'No fue posible guardar la entrada.' }
        : { ok: true, postId: savedId };
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') throw error;
      return { ok: false, message: 'No fue posible guardar la entrada.' };
    }
  }

  async openDm(targetAgentId: string, signal?: AbortSignal): Promise<AgencyOpenDmResult> {
    try {
      const conversationId = await this.rpc<string>('agency_open_dm_v1', { p_target_agent_id: targetAgentId }, signal);
      return conversationId === null ? { ok: false, message: 'No fue posible abrir el DM.' } : { ok: true, conversationId };
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') throw error;
      return { ok: false, message: 'No fue posible abrir el DM.' };
    }
  }

  async loadMessages(conversationId: string, signal?: AbortSignal): Promise<ConversationMessagesResult> {
    try {
      const rows = await this.rpc<MessageRow[]>('conversation_messages_v1', { p_conversation_id: conversationId, p_before: null, p_limit: 60 }, signal);
      if (rows === null) return { ok: false, message: 'No fue posible leer el DM.' };
      const messages: ConversationMessage[] = rows.map((row) => ({
        messageId: row.message_id, body: row.body, messageType: row.message_type, createdAt: row.created_at,
        senderAgentId: row.sender_agent_id, senderName: row.sender_name, isMine: row.is_mine,
      })).reverse();
      return { ok: true, messages };
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') throw error;
      return { ok: false, message: 'No fue posible conectar con el DM.' };
    }
  }

  async sendMessage(conversationId: string, body: string, signal?: AbortSignal): Promise<SendMessageResult> {
    try {
      const messageId = await this.rpc<string>('workspace_send_message_v1', { p_conversation_id: conversationId, p_body: body }, signal);
      return messageId === null ? { ok: false, message: 'No fue posible enviar el mensaje.' } : { ok: true, messageId };
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') throw error;
      return { ok: false, message: 'No fue posible enviar el mensaje.' };
    }
  }
}
